import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import type { Op } from 'quill/core';
import React, { useState } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import ScrollArea from '../../components/ScrollArea';
import Tooltip from '../../components/Tooltip';
import { useQuill } from '../../editor/QuillContext';
import { CREATION_ID, EditorActions } from '../../editor/editorSlice';
import type { Mention } from '../../editor/types';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import mentionIcon from '../../icons/non-prefilled/16x16/mention.svg';
import { UIActions } from '../../ui/uiSlice';
import updateApolloList from '../../utils/graphql/updateApolloList';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import useIsPublicView from '../../utils/useIsPublicView';
import {
  MagneticWallItemActionContainer,
  MagneticWallItemButtonGroup,
  MagneticWallItemEmojiPickerButton,
  MagneticWallItemIconButton,
  MagneticWallItemSubmitButton,
  MagneticWallItemWithAvatar,
} from '../MagneticWallItem';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import isContentEmpty from '../utils/isContentEmpty';
import styles from './CommentCreate.module.scss';
import CommentInput from './CommentInput';
import CommentWarning from './CommentWarning';
import GroupNotificationCommentWarning from './GroupNotificationCommentWarning';
import showFullPageSheet from './showFullPageSheet';
import getCurrentMarkForComment from './utils/getCurrentMarkForComment';

const POST_QUERY = gql(`
  query CommentCreate($postId: ID!) {
    post(id: $postId) {
      id
      ...usePostPolicyCheck_post
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const ADD_COMMENT_MUTATION = gql(`
  mutation addComment(
    $id: ID!
    $content: String!
    $notifyGroups: Boolean = false
  ) {
    addComment(threadId: $id, content: $content, notifyGroups: $notifyGroups) {
      ...CommentFields
    }
  }
`);

const ADD_COMMENT_QUERY = gql(`
  query addCommentQuery($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        # eslint-disable-next-line slab-graphql/no-unused-fields
        comments {
          id
        }
      }
    }
  }
`);

const CREATE_COMMENT_MUTATION = gql(`
  mutation createComment(
    $id: ID!
    $version: Int!
    $checksum: Int!
    $mark: Json!
    $content: String!
    $notifyGroups: Boolean = false
  ) {
    createComment(
      postId: $id
      version: $version
      checksum: $checksum
      mark: $mark
      content: $content
      notifyGroups: $notifyGroups
    ) {
      id
      comments {
        ...CommentFields
      }
      reactions {
        id
      }
    }
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentCreate_thread on PublicThreadFields {
    id
    author {
      id
    }
  }
`);

interface CommentCreateProps {
  initialContent?: Op[];
  postId: string;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onChange?: (content: Op[]) => void;
  onResize?: () => void;
  onCreated?: () => void;
  disabled: boolean;
}

function isOpWithGroupMention(op: Op): op is Op & {
  insert: { mention: Mention & { type: 'group' } };
} {
  const { insert } = op;
  if (insert != null && typeof insert !== 'string' && insert.mention != null) {
    const mention = insert.mention as Mention;
    return mention.type === 'group';
  }
  return false;
}

const CommentCreate = ({
  initialContent = [],
  postId,
  thread,
  onChange,
  onResize,
  onCreated,
  disabled,
}: CommentCreateProps) => {
  const isPublicView = useIsPublicView();
  const [commentInputKey, setCommentInputKey] = useState(() => _.uniqueId());
  const [content, setContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const [isGroupNotifyingEnabled, setIsGroupNotifyingEnabled] = useState(true);

  const isNewThread = thread.id === CREATION_ID;

  const mentionedGroupIds = content
    ? _.uniq(
        content.filter(isOpWithGroupMention).map((op) => op.insert.mention.id),
      )
    : [];

  const { quill } = useQuill();

  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const currentUser = useAppSelector((state) => state.session.user);
  const sync = useAppSelector((state) => state.editor.sync);

  const dispatch = useAppDispatch();

  const { data: { post } = {} } = useQuery(POST_QUERY, {
    variables: { postId },
    skip: isPublicView,
  });

  const [addCommentMutation] = useMutation(ADD_COMMENT_MUTATION);

  const [createCommentMutation] = useMutation(CREATE_COMMENT_MUTATION, {
    update: legacyUpdateQueryList(
      { query: ADD_COMMENT_QUERY, variables: { postId } },
      'post.threads',
      'createComment',
      true,
    ),
  });

  const addComment = (commentContent: string) =>
    addCommentMutation({
      variables: {
        content: commentContent,
        id: thread.id,
        notifyGroups: isGroupNotifyingEnabled,
      },
      update: (proxy, { data }) => {
        try {
          const prev = proxy.readQuery({
            query: ADD_COMMENT_QUERY,
            variables: { postId },
          });
          if (!prev || !data) {
            return;
          }
          const threads = prev.post?.threads ?? [];
          const index = threads.findIndex((t) => t.id === thread.id);
          if (index === -1) {
            return;
          }
          const updated = updateApolloList(
            prev,
            data,
            `post.threads.${index}.comments`,
            'addComment',
            true,
          );
          proxy.writeQuery({
            query: ADD_COMMENT_QUERY,
            variables: { postId },
            data: updated,
          });
        } catch (e) {
          // readQuery failed
        }
      },
    });

  const createComment = async (commentContent: string, mark: string) => {
    if (!sync) throw new Error('sync not inited');

    return createCommentMutation({
      variables: {
        content: commentContent,
        mark,
        id: postId,
        version: sync.version,
        checksum: sync.checksum,
        notifyGroups: isGroupNotifyingEnabled,
      },
    });
  };

  const handleChange = (newContent: Op[]) => {
    setContent(newContent);
    onChange?.(newContent);
    onResize?.();
  };

  const handleSave = async () => {
    if (!content.length) {
      return;
    }

    setIsSaving(true);

    const json = JSON.stringify(content);

    try {
      if (isNewThread) {
        if (!quill) throw new Error('quill is not ready');
        const { data } = await createComment(
          json,
          JSON.stringify(getCurrentMarkForComment(quill)),
        );
        handleChange([]);
        setIsSaving(false);
        if (data?.createComment.id) {
          dispatch(
            EditorActions.focusComment({
              id: data.createComment.id,
              isActive: true,
              hasExpandIntent: true,
            }),
          );
        }
      } else {
        await addComment(json);
        handleChange([]);
        setIsSaving(false);
      }
      onCreated?.();
      setCommentInputKey(_.uniqueId());
      setIsGroupNotifyingEnabled(true);
    } catch (err) {
      setIsSaving(false);
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not create the comment. Please try again or contact support.',
        }),
      );
    }
  };

  const shouldAutoFocus =
    (!showFullPageSheet() || isNewThread) &&
    (readOnly || !quill?.hasFocus()) &&
    !disabled;

  const postPolicyCheck = usePostPolicyCheck();

  return (
    <div>
      <CommentInput
        key={commentInputKey}
        autoFocus={shouldAutoFocus}
        initialContent={content}
        onChange={handleChange}
        onSave={handleSave}
        disabled={disabled}
      >
        {({ input, mentionUser, insertText }) => (
          <div className={styles.container}>
            <MagneticWallItemWithAvatar
              avatar={
                <div className={styles.avatar}>
                  <MagneticWallItemAvatar user={currentUser} />
                </div>
              }
              disabled={disabled}
            >
              <ScrollArea
                className={classNames(styles.editor, {
                  [styles.newThread]: isNewThread,
                })}
              >
                {input}
              </ScrollArea>
            </MagneticWallItemWithAvatar>
            <div className={styles.actions}>
              <MagneticWallItemActionContainer>
                <MagneticWallItemEmojiPickerButton
                  onPick={insertText}
                  disabled={disabled}
                />
                {currentUser.type === UserType.Visitor ? null : (
                  <MagneticWallItemIconButton
                    icon={mentionIcon}
                    onClick={() => mentionUser()}
                    tooltip="Mention someone"
                    disabled={disabled}
                  />
                )}
                <MagneticWallItemButtonGroup>
                  <MagneticWallItemSubmitButton
                    disabled={disabled || isSaving || isContentEmpty(content)}
                    onClick={handleSave}
                  >
                    Comment
                  </MagneticWallItemSubmitButton>
                </MagneticWallItemButtonGroup>
              </MagneticWallItemActionContainer>
            </div>
          </div>
        )}
      </CommentInput>
      {mentionedGroupIds.length > 0 && (
        <GroupNotificationCommentWarning
          groupIds={mentionedGroupIds}
          onChange={setIsGroupNotifyingEnabled}
          isNotifyingEnabled={isGroupNotifyingEnabled}
        />
      )}
      {post &&
        !postPolicyCheck(
          PostPolicyAction.EditPost,
          post,
          thread.author ?? currentUser, // If thread does not have an author, this is a new comment thread
        ) && (
          <Tooltip
            trigger="clickOrHover"
            display="block"
            placement="bottom"
            content={
              'Comment threads authored\nby users without post edit\naccess are only visible to\nthem and post editors'
            }
          >
            <CommentWarning>This comment has limited visibility</CommentWarning>
          </Tooltip>
        )}
    </div>
  );
};

export default CommentCreate;
